import { Injectable } from '@angular/core';
import { Observable, Subscription, distinctUntilChanged, filter, map, take, tap, zip } from 'rxjs';
import { ActivatedRoute, DetachedRouteHandle, NavigationEnd, NavigationExtras, Params, Router } from '@angular/router';
import { PlatformLocation } from '@angular/common';

import { GlobalFacade } from '@app/global.facade';
import { HeaderPageType } from '@header/entities/ds-header-config';
import { AppFacade } from '@app/app.facade';
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { userHistoryRoute } from '@user-history/constants/history-routes.constants';
import { endCallWebView } from '@app/modules/core/util/redirect-login';
import { isActiveMobileInterface } from '@app/modules/core/mappers/mobile-interface.mapper';
import { DetachedRouteHandlesService } from '@app/services/detached-route-handles.service';

@Injectable()
export class NavigationService {

  private _lastValue: number;
  private _newValue: number;
  private _lastIsOutlet: boolean;
  private _newIsOutlet: boolean;
  private _animationValue: number;

  private _transitions: Subscription;

  private _previousPath: string;
  private _currentPath: string;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly globalFacade: GlobalFacade,
    private readonly platformLocation: PlatformLocation,
    private readonly appFacade: AppFacade,
    private readonly detachedRouteHandlesService: DetachedRouteHandlesService,
  ) { }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get events$(): Observable<any> {
    return this.router.events.pipe(
      map((event) => ({ rootRoute: this.rootRoute, event })),
      filter(({ rootRoute }) => rootRoute.outlet === 'primary'),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get getDetachedChangesByComponent$(): Observable<{ changes: Map<any, DetachedRouteHandle>, component: any }> {
    return this.detachedRouteHandlesService.getChangesByComponent$(this.component);
  }

  get rootRoute(): ActivatedRoute {
    let route = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  get url(): string {
    return this.getPathWithoutQueryParams(this.router.url);
  }

  get params(): Params {
    return this.activatedRoute?.snapshot?.paramMap?.['params'];
  }

  get queryParams(): Params {
    return this.activatedRoute?.snapshot?.queryParamMap?.['params'];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get component(): any {
    return this.activatedRoute.component;
  }

  get animationValue(): number {
    return this._animationValue;
  }

  private get activatedRoute(): ActivatedRoute {
    return this.rootRoute?.outlet === 'primary' ? this.rootRoute : this.rootRoute?.parent;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get getNavigationState(): { [k: string]: any } {
    return this.router.getCurrentNavigation()?.extras?.state;
  }

  public detectPathChange$(routeScope: string): Observable<{ params: Params, queryParams: Params }> {
    this._currentPath = this.url;
    this._previousPath = null;
    return this.router.events.pipe(
      filter((event) => {
        const _url = this.getPathWithoutQueryParams((event as NavigationEnd)?.url);
        return event instanceof NavigationEnd && _url && this._currentPath !== _url &&
          _url.includes(routeScope) && this.rootRoute?.outlet === 'primary';
      }),
      distinctUntilChanged(() => this._previousPath === this._currentPath),
      tap((event: NavigationEnd) => {
        this._previousPath = this._currentPath;
        this._currentPath = this.getPathWithoutQueryParams(event.url);
      }),
      map(() => ({
        params: this.params,
        queryParams: this.queryParams
      })),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public navigateOutlet(outlet: string, path: string, state: { [k: string]: any } | undefined = undefined): void {
    this.router.navigate([{ outlets: { [outlet]: [path] } }], {
      ...(!isNullOrUndefined(state)) && { state: state },
      relativeTo: this.rootRoute, skipLocationChange: true
    });
  }

  public navigateToBack(): void {
    zip(
      this.globalFacade.ui.navigation$, this.appFacade.closeWebViewInDetail$, this.appFacade.user$
    ).pipe(
      take(1),
      tap(([navigation, closeWebViewInDetail, user]) => {
        if ((closeWebViewInDetail && isActiveMobileInterface()) || (navigation?.length === 1)) {
          endCallWebView();
        } else if (navigation?.length) {
          const currentPage = navigation.at(-1);
          const nextPage = navigation.slice(0, -1)?.at(-1);
          const extras = isEmpty(nextPage?.Extras) ? { queryParams: {} } : nextPage?.Extras;
          if (
            nextPage?.PageType !== HeaderPageType.MyTripsPage &&
            (currentPage?.PageType === HeaderPageType.LastTripDetailPage ||
              currentPage?.PageType === HeaderPageType.TripDetailPage)
          ) {
            this.router.navigate([`${userHistoryRoute}/${user?.id}`], extras);
          } else {
            this.router.navigate([nextPage?.Url], extras);
          }
          this.appFacade.removeLastNavigationItem();
        }
      })
    ).subscribe().unsubscribe();
  }

  public onPopState(): void {
    this.platformLocation.onPopState(() => {
      this.globalFacade.ui.navigation$.pipe(
        take(1),
        tap((navigation) => {
          const currentPage = navigation.find(item => item.Url.includes(location.pathname));
          const lastPage = navigation?.at(-1);
          if (currentPage && currentPage.Url !== lastPage.Url) {
            this.appFacade.removeLastNavigationItem();
          }
        })
      ).subscribe().unsubscribe();
    });
  }

  public initTransitions(): void {
    this._transitions = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap(() => {
        this._newIsOutlet = this.rootRoute?.outlet !== 'primary';
        if (!this._newIsOutlet && !this._lastIsOutlet && this.rootRoute?.snapshot?.data?.routingLevel) {
          this._lastValue = this._newValue;
          this._newValue = this.rootRoute.snapshot.data.routingLevel;
          const animateLevel = this.getAnimationNumber(this._lastValue, this._newValue);
          if (animateLevel === this._animationValue && animateLevel === 1) {
            this._animationValue = this._animationValue - 1;
          } else {
            this._animationValue = animateLevel;
          }
        }
        this._lastIsOutlet = this._newIsOutlet;
      })
    ).subscribe();
  }

  public stopTransitions(): void {
    this._transitions?.unsubscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public navigate(commands: any[], extras?: NavigationExtras): void {
    this.router.navigate(commands, extras);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetchStateByKey(key: string): any {
    return this.getNavigationState?.[key];
  }

  private getAnimationNumber(_lastValue: number, _newValue: number): number {
    return (_lastValue && _newValue) ? (_lastValue ? ((_newValue > _lastValue) ? 2 : 1) : 1) : 0;
  }

  private getPathWithoutQueryParams(path: string): string {
    return path && path.includes('?') ? path.split('?')[0] : path;
  }
}
